import { ErrorHandler, Injectable, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { toast } from '@spartan-ng/brain/sonner';

/**
 * This service is for surfacing errors in the UI. By default, we'll notify the user with a snackbar (toast) message.
 * You should only need to include this service if you need to surface errors in the ui or need to notify the user
 * of errors.
 */
@Injectable({
  providedIn: 'root'
})
export class AppErrorHandler extends ErrorHandler {
  private log = inject(NGXLogger);

  public errorEvent: Subject<Error> = new Subject<any>();

  /**
   * Create a snackbar notification
   * @param {string} notification - The notification message
   * @param {number} notificationCode - An error code or Http Status
   */
  notifyUserSnackbar(notification: string, notificationCode?: number) {
    toast.error('Oops!', { description: `${ notificationCode ? notificationCode + ': ' : '' }${ notification }` });
  }

  /**
   * Trial-expiry gate (HTTP 402, type "trial_expired") — an expected lifecycle
   * state, not an error, so it gets warn styling instead of the generic toast.
   */
  notifyTrialExpired(notification: string) {
    toast.warning('Free trial ended', { description: notification });
  }

  /**
   * This function is a global javascript error handler. It will catch all javascript errors
   * produced within the application. The docs at https://angular.io/api/core/ErrorHandler say
   * that err has a type of any. I'm assuming the "any" type is so you can pass it a custom error
   * or error event. console logging the err argument just outputs the stack trace.
   *
   * If an error occurs within a try/catch block, this will not be fired unless you
   * throw the error within the catch block.
   *
   * I understand I've got repeated code below, but this is merely a placeholder so that
   * custom logic can be introduced based on the error type (err.name). You probably don't
   * want to break it down to that level but instead send to a DB, throw a dialog
   * for debugging, etc.
   *
   * Definition is in @see{src/app/core/core.module.ts}
   *
   * @param err {Error}
   */
  override handleError(err: Error) {
    let message = err?.message || 'An error occurred. Please try again or contact support for help!';
    if (!message && 'error' in err && 'message' in (err.error as Object)) {
      message = (err.error as { message?: string })['message'] || 'An error occurred. Please try again or contact support for help!';
    }

    this.log.error(message, err);
    this.errorEvent.next(err);
  }
}
